import { motion } from 'framer-motion'
import Badge from '@/components/ui/Badge'
import type { EvaluationGrade } from '@/types/common'
import { cn } from '@/lib/utils'

interface EvaluationItem {
  /** 항목 라벨 */
  label: string
  /** 점수 (0~100) */
  score: number
}

interface EvaluationChartProps {
  /** 종합 등급 (없을 수 있음) */
  grade?: EvaluationGrade | string | null
  /** 종합 점수 */
  totalScore: number
  /** 항목별 점수 */
  items: EvaluationItem[]
  /** 추가 CSS 클래스 */
  className?: string
}

/** 점수에 따른 바 그라데이션 색상 (높으면 초록-파랑, 낮으면 주황-빨강) */
function getBarGradient(score: number): string {
  if (score >= 90) return 'from-emerald-400 to-teal-500'
  if (score >= 75) return 'from-blue-400 to-indigo-500'
  if (score >= 60) return 'from-amber-400 to-yellow-500'
  if (score >= 40) return 'from-orange-400 to-amber-500'
  return 'from-red-400 to-orange-500'
}

/** 점수에 따른 텍스트 색상 */
function getScoreColor(score: number): string {
  if (score >= 90) return 'text-emerald-600'
  if (score >= 75) return 'text-blue-600'
  if (score >= 60) return 'text-amber-600'
  if (score >= 40) return 'text-orange-600'
  return 'text-red-600'
}

/**
 * 평가 항목별 점수 바 차트 컴포넌트 v2.0
 *
 * - 종합 등급 배지 + 종합 점수 (더 크고 명확)
 * - 바 높이: 12px, 라운드: 6px
 * - 점수별 그라데이션 컬러 (높으면 초록-파랑, 낮으면 주황-빨강)
 * - 배경 바: 더 연한 색
 * - 애니메이션: 바 진입 시 슬라이드
 */
export default function EvaluationChart({
  grade,
  totalScore,
  items,
  className,
}: EvaluationChartProps) {
  return (
    <div className={className}>
      {/* 종합 등급 - 그라데이션 배경 */}
      <div
        className="flex items-center justify-center gap-4 py-6 rounded-2xl"
        style={{ background: 'var(--gradient-result)' }}
      >
        <div className="text-center">
          <p className="text-body-sm font-medium text-cb-gray-500 mb-3">종합 평가</p>
          <div className="flex items-center justify-center gap-3">
            {grade && (
              <Badge variant="grade" grade={grade as EvaluationGrade} className="text-h3 px-4 py-1.5 shadow-sm" />
            )}
            <span className="text-display font-bold text-cb-gray-900 text-amount">
              {totalScore.toFixed(2)}<span className="text-h2 text-cb-gray-500 ml-0.5">점</span>
            </span>
          </div>
        </div>
      </div>

      {/* 항목별 점수 */}
      <div className="mt-6 flex flex-col gap-4">
        {items.map((item, index) => {
          const percentage = Math.min(Math.round(item.score), 100)
          return (
            <div key={item.label} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="text-body-sm font-medium text-cb-gray-700">{item.label}</span>
                <span className={cn('text-body-sm font-bold text-amount', getScoreColor(item.score))}>
                  {item.score}점
                </span>
              </div>
              <div
                className="w-full overflow-hidden rounded-[6px] bg-cb-gray-100 h-3"
                role="progressbar"
                aria-valuenow={item.score}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${item.label} ${item.score}점`}
              >
                <motion.div
                  className={cn('h-full rounded-[6px] bg-gradient-to-r', getBarGradient(item.score))}
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ duration: 0.6, delay: index * 0.1, ease: 'easeOut' }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
